/**
 * computeClientAggregates — pure client-side Σ over an in-memory row set
 * (Operator Lists v2.1, Task 7). Used by the selection-scoped footer override:
 * unlike `runtimeApi.aggregate` (Task 1, server-side over the filtered set),
 * this runs entirely in the browser over the rows currently held in
 * `useRowSelection`. Mirrors the backend aggregate ops so values agree
 * whether the source is the server or the client (empty set → `null` for
 * numeric ops, `0` for `count`).
 */
export function computeClientAggregates(
  rows: Record<string, unknown>[],
  aggregates: string[],
): Record<string, number | null> {
  const out: Record<string, number | null> = {}
  for (const pair of aggregates) {
    const idx = pair.indexOf(':')
    const field = idx === -1 ? pair : pair.slice(0, idx)
    const op = idx === -1 ? '' : pair.slice(idx + 1)
    if (op === 'count') {
      out[pair] = rows.reduce((n, r) => (r[field] != null && r[field] !== '' ? n + 1 : n), 0)
      continue
    }
    const nums = rows
      .map((r) => r[field])
      .filter((v) => v != null && v !== '')
      .map((v) => Number(v))
      .filter((n) => !Number.isNaN(n))
    if (nums.length === 0) {
      out[pair] = null
      continue
    }
    if (op === 'sum') out[pair] = nums.reduce((a, b) => a + b, 0)
    else if (op === 'avg') out[pair] = nums.reduce((a, b) => a + b, 0) / nums.length
    else if (op === 'min') out[pair] = Math.min(...nums)
    else if (op === 'max') out[pair] = Math.max(...nums)
    else out[pair] = null
  }
  return out
}
